import { isFullDayLeave } from "@/requests/leaves/lib/isFullDayLeave"
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Filter, FileText, ClipboardList, Watch, Files, File, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getLeaveRequestsByUser, type LeaveRequest } from '@/lib/collections/requestsLeave/user/getCertificateRequestsByUser ';
// import { getApprovalConfig, type ApprovalRole } from '@/requests/leaves/config/leaveApprovals';
import { formatDuration } from '../../lib/calculateLeaveDuration';
import { toast } from 'react-toastify';
import AddRequestLeave from './addRequestLeave';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';
import { fetchUsersWithProfiles } from '@/lib/manager realtime/userCache';
import EditRequestLeave from './updateRequestLeave';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';


type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type ApprovalStatus = 'pending' | 'approved' | 'rejected';

const ITEMS_PER_PAGE = 6;

const STATUS_CONFIG: Record<ApprovalStatus, {
    label: string;
    variant: 'default' | 'secondary' | 'destructive';
    icon: any;
    className?: string;
}> = {
    pending: {
        label: 'Pendiente',
        variant: 'secondary',
        className: 'bg-yellow-600 dark:bg-yellow-800 text-white dark:text-white',
        icon: Clock,
    },
    approved: {
        label: 'Aprobado',
        variant: 'default',
        icon: CheckCircle2,
        className: 'bg-green-500 hover:bg-green-600 text-white dark:bg-green-600 dark:text-white',
    },
    rejected: {
        label: 'Rechazado',
        variant: 'destructive',
        icon: XCircle,
    },
};

// UTILITY FUNCTIONS

const getOverallStatus = (request: LeaveRequest): ApprovalStatus => {
    if (request.status === 'rejected') {
        return 'rejected';
    }

    if (request.status === 'approved') {
        return 'approved';
    }

    return 'pending';
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// SUBCOMPONENTS

const StatusBadge = ({ status }: { status: ApprovalStatus }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;

    return (
        <Badge
            variant={config.variant}
            className={`flex items-center gap-1 w-fit ${config.className || ''}`}
        >
            <Icon className="w-3 h-3" />
            {config.label}
        </Badge>
    );
};

// const ApprovalStep = ({
//     status,
//     label,
//     showLine = true,
//     stepNumber
// }: {
//     status: string;
//     label: string;
//     showLine?: boolean;
//     stepNumber?: number;
// }) => {
//     const getStepColor = () => {
//         if (status === 'approved') return 'bg-green-500';
//         if (status === 'rejected') return 'bg-red-500';
//         return 'bg-gray-300';
//     };

//     const getStepIcon = () => {
//         if (status === 'approved') return <CheckCircle2 className="w-4 h-4 text-white" />;
//         if (status === 'rejected') return <XCircle className="w-4 h-4 text-white" />;
//         return <span className="text-xs text-gray-600">{stepNumber}</span>;
//     };

//     return (
//         <>
//             <div className="flex items-center gap-1.5 min-w-0">
//                 <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getStepColor()}`}>
//                     {getStepIcon()}
//                 </div>
//                 <span className="text-xs font-medium whitespace-nowrap">{label}</span>
//             </div>
//             {showLine && (
//                 <div className={`h-0.5 w-3 sm:w-4 shrink-0 sm:mx-0 ${status === 'approved' ? 'bg-green-500' : 'bg-gray-300'}`} />
//             )}
//         </>
//     );
// };

// const ApprovalSteps = ({ request }: { request: LeaveRequest }) => {
//     const config = getApprovalConfig();
//     const requiredApprovals = config.requiredApprovals;

//     const approvalData: Record<ApprovalRole, { status: string; label: string }> = {
//         rrhh: { status: request.statusByRrhh, label: 'RRHH' },
//         supervisor: { status: request.statusBySupervisor, label: 'Supervisor' },
//         manager: { status: request.statusByManager, label: 'Gerencia' }
//     };

//     return (
//         <div className="flex items-center gap-1.5 sm:gap-2 mt-3">
//             {requiredApprovals.map((role, index) => {
//                 const data = approvalData[role];
//                 const isLast = index === requiredApprovals.length - 1;

//                 return (
//                     <ApprovalStep
//                         key={role}
//                         status={data.status}
//                         label={data.label}
//                         showLine={!isLast}
//                         stepNumber={index + 1}
//                     />
//                 );
//             })}
//         </div>
//     );
// };

const RejectionNotice = ({ reason }: { reason: string }) => {
    return (
        <div className=" border border-red-200 rounded-md p-3">
            <p className="text-xs font-medium text-red-800 dark:text-red-500 mb-1">Motivo del rechazo:</p>
            <p className="text-xs text-red-300 dark:text-gray-400">{reason}</p>
        </div>
    );
};

const EmptyState = ({ hasRequests }: { hasRequests: boolean }) => {
    return (
        <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                    {hasRequests ? 'No se encontraron solicitudes' : 'No tienes solicitudes de permiso'}
                </h3>
                <p className="text-muted-foreground text-center px-4">
                    {hasRequests
                        ? 'Intenta cambiar los filtros de búsqueda'
                        : 'Crea tu primera solicitud haciendo clic en "Registrar solicitud"'
                    }
                </p>
            </CardContent>
        </Card>
    );
};

const RequestCard = ({ request, getUserName, onSuccess }: { request: LeaveRequest; getUserName: (id: string) => string; onSuccess?: () => void; }) => {
    const overallStatus = getOverallStatus(request);
    const rejectionReason = request.rejectionReasonByRrhh ||
        request.commentsSupervisor ||
        request.commentsManager;
    const [commentOpen, setCommentOpen] = useState(false);


    return (

        <>

            <Card className="hover:shadow-md transition-shadow overflow-hidden border-l-4 border-l-amber-400  dark:border-l-amber-500  dark:hover:border-l-amber-600 hover:border-l-amber-500">
                <CardHeader className="overflow-hidden">
                    <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <CardTitle className="text-lg wrap-break-word overflow-wrap-anywhere">
                                    {request.title}
                                </CardTitle>
                                <StatusBadge status={overallStatus} />
                            </div>
                            <CardDescription className="space-y-1.5">
                                <div className="flex items-center gap-1.5">
                                    <Clock className="w-3 h-3 shrink-0" />
                                    <span className="text-xs">
                                        {formatDateShort(request.startDate)} - {formatDateShort(request.endDate)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">
                                        <ClipboardList className='w-3' /> Solicitado: {formatDateShort(request.$createdAt)}
                                    </span>
                                </div>
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {request.comment && (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="cursor-pointer gap-1"
                                        onClick={() => setCommentOpen(true)}
                                    >
                                        <FileText className="w-3 h-3" />
                                        Comentario
                                    </Button>

                                    <Dialog open={commentOpen} onOpenChange={setCommentOpen}>
                                        <DialogContent className="max-w-md">
                                            <DialogHeader>
                                                <DialogTitle>Comentario de la solicitud</DialogTitle>
                                            </DialogHeader>
                                            <div className="bg-muted/30 rounded-md p-4">
                                                <p className="text-sm whitespace-pre-wrap wrap-break-word">
                                                    {request.comment}
                                                </p>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </>
                            )}

                            {(overallStatus === 'pending' || overallStatus === 'rejected') && (
                                <EditRequestLeave request={request} onSuccess={onSuccess} />
                            )}
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4 overflow-hidden">
                    {/* Info adicional */}
                    <div className="text-sm space-y-1">

                        {!isFullDayLeave(request) && (
                            <div className="text-xs text-muted-foreground bg-muted/30 rounded-md p-2 space-y-1">
                                <p className="flex items-center gap-1.5">
                                    <span className="font-medium">Inicio:</span>
                                    {formatDate(request.startDate)}
                                </p>
                                <p className="flex items-center gap-1.5">
                                    <span className="font-medium">Fin:</span>
                                    {formatDate(request.endDate)}
                                </p>
                            </div>
                        )}


                        <div className="flex flex-wrap items-center gap-1 text-sm">
                            <Badge variant="outline" className="gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDuration(request.totalDuration)}
                            </Badge>
                            <Badge variant={isFullDayLeave(request) ? "default" : "secondary"} className="gap-1">
                                {isFullDayLeave(request) ? <ClipboardList /> : <Watch />}
                                {isFullDayLeave(request) ? ' Todo el día' : ' Parcial'}
                            </Badge>
                        </div>
                    </div>

                    {/* {request.comment && (
                        <p className="text-sm text-muted-foreground line-clamp-2 wrap-break-word overflow-wrap-anywhere">
                            {request.comment}
                        </p>
                    )} */}

                    {request.approvedById && overallStatus === 'approved' && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                            <span>Aprobado por: <span className="font-medium text-foreground">{getUserName(request.approvedById)}</span></span>
                        </div>
                    )}

                    {request.idRejectedByRrhh && overallStatus == 'rejected' && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                            <span>Rechazado por: <span className="font-medium text-foreground">{getUserName(request.idRejectedByRrhh)}</span></span>
                        </div>
                    )}


                    {overallStatus === 'rejected' && rejectionReason && (
                        <RejectionNotice reason={rejectionReason} />
                    )}


                </CardContent>
            </Card></>
    );
};

const Pagination = ({
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    total,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    total: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <p className="text-sm text-muted-foreground">
                Mostrando {startIndex + 1}-{endIndex} de {total}
            </p>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center px-3 text-sm font-medium">
                    {currentPage} / {totalPages}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};



const EmployeeLeaveRequestsView = () => {
    const { user } = useAuth();
    const [usersCache, setUsersCache] = useState<Map<string, string>>(new Map());

    // State
    const [requests, setRequests] = useState<LeaveRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);


    const getUserName = (userId: string): string => {
        return usersCache.get(userId) || 'Usuario desconocido';
    };

    const loadUsersCache = async () => {
        try {
            const users = await fetchUsersWithProfiles();
            const cache = new Map<string, string>();
            users.forEach(u => cache.set(u.id, u.name));
            setUsersCache(cache);
        } catch (err) {
            console.error('Error loading users cache:', err);
        }
    };

    useEffect(() => {
        loadUsersCache();
    }, []);

    // Load requests
    useEffect(() => {
        if (user?.$id) {
            loadRequests();
        }
    }, [user?.$id]);

    const loadRequests = async () => {
        if (!user?.$id) return;

        setLoading(true);
        const result = await getLeaveRequestsByUser(user.$id);

        if (result.success && result.data) {
            setRequests(result.data);
        } else {
            toast.error('Error al cargar las solicitudes');
        }
        setLoading(false);
    };

    // Filtering and pagination
    const filteredRequests = requests.filter(request => {
        const overallStatus = getOverallStatus(request);
        const matchesStatus = statusFilter === 'all' || overallStatus === statusFilter;
        const matchesSearch =
            searchTerm === '' ||
            (request.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (request.comment?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        return matchesStatus && matchesSearch;
    });

    const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredRequests.length);
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    const handleFilterChange = () => {
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (


        <><Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <div className="flex items-center gap-1.5">
                        <Files />
                        <BreadcrumbPage>Permisos</BreadcrumbPage>
                    </div>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                        <Link to="/requests">Menu</Link>
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                    <BreadcrumbPage className="hidden sm:block">Permisos</BreadcrumbPage>
                    <BreadcrumbPage className="block sm:hidden">
                        <File className="w-5 h-5" />
                    </BreadcrumbPage>
                </BreadcrumbItem>
            </BreadcrumbList>
        </Breadcrumb>

            <div className="py-12 pb-24">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-2xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">Solicitudes de Permiso</h2>
                        <p className="text-muted-foreground text-sm">Gestiona tus permisos de salida</p>
                    </div>
                    <AddRequestLeave onSuccess={loadRequests} />
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Input
                            placeholder="Buscar por título o comentario..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                handleFilterChange();
                            }}
                            className="pl-9" />
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>

                    <Select
                        value={statusFilter}
                        onValueChange={(v) => {
                            setStatusFilter(v as StatusFilter);
                            handleFilterChange();
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-48">
                            <SelectValue placeholder="Filtrar por estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="pending">Pendientes</SelectItem>
                            <SelectItem value="approved">Aprobados</SelectItem>
                            <SelectItem value="rejected">Rechazados</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Content */}
                {paginatedRequests.length === 0 ? (
                    <EmptyState hasRequests={requests.length > 0} />
                ) : (
                    <>
                        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                            {paginatedRequests.map((request) => (
                                <RequestCard key={request.$id} request={request} getUserName={getUserName} onSuccess={loadRequests} />))}
                        </div>


                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            startIndex={startIndex}
                            endIndex={endIndex}
                            total={filteredRequests.length}
                            onPageChange={setCurrentPage} />
                    </>
                )}
            </div>
        </>
    );
};

export default EmployeeLeaveRequestsView;